import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2, ArrowLeft, Check, Gift } from "lucide-react";
import { LogoMark } from "@/components/Brand";
import { toast } from "sonner";
import { SPECIALITIES } from "@/components/SpecialityAccessFields";
import { useFounderPlaces } from "@/components/FoundersClub";

const PERKS = [
  "AI compliance assistant with human escalation",
  "CQC, Ofsted & clinical aesthetics policy packs and mock interviews",
  "Live regulatory updates from CQC & Ofsted",
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { state } = useLocation();
  const nextPath = params.get("next")?.startsWith("/") ? params.get("next") : "/dashboard";
  const ref = (params.get("ref") || "").toUpperCase();
  const [referrer, setReferrer] = useState(null);
  const [form, setForm] = useState({ name: "", org_name: "", email: state?.email || "", password: "", speciality: params.get("speciality") || "" });
  const places = useFounderPlaces();
  const leftFor = (s) => places?.places.find((p) => p.speciality === s)?.left ?? null;
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ref) api.get(`/referrals/validate/${ref}`).then((r) => setReferrer(r.data)).catch(() => setReferrer(null));
  }, [ref]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const [consent, setConsent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register({ ...form, consent, ...(referrer ? { ref } : {}) });
      toast.success("Account created — welcome aboard");
      navigate(nextPath);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background order-2 lg:order-1">
        <div className="w-full max-w-sm animate-fade-up">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <h2 className="font-display text-3xl font-bold tracking-tight mb-1">Create your account</h2>
          <p className="text-muted-foreground mb-8 text-sm">Start your compliance journey in minutes.</p>
          {referrer && (
            <div className="mb-6 flex items-start gap-3 rounded-xl border border-accent/30 bg-accent/10 p-3 text-sm" data-testid="referral-banner">
              <Gift className="w-4 h-4 text-accent mt-0.5 shrink-0" />
              <p>Invited by <span className="font-semibold">{referrer.referrer}</span> — you'll get <span className="font-semibold">{referrer.percent_off}% off</span> your first month's subscription.</p>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="name">Your name</Label>
              <Input id="name" value={form.name} onChange={set("name")} required
                data-testid="register-name-input" placeholder="Jane Smith" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="org">Organisation</Label>
              <Input id="org" value={form.org_name} onChange={set("org_name")}
                data-testid="register-org-input" placeholder="Sunrise Care Ltd" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={set("email")} required
                data-testid="register-email-input" placeholder="you@careservice.co.uk" className="mt-1.5" />
            </div>
            <div>
              <Label>Your speciality</Label>
              <div className="grid gap-1.5 mt-1.5" role="radiogroup" data-testid="register-speciality">
                {SPECIALITIES.map(([v, l]) => {
                  const left = leftFor(v); const on = form.speciality === v;
                  return (
                    <button key={v} type="button" role="radio" aria-checked={on} onClick={() => setForm((f) => ({ ...f, speciality: v }))} data-testid={`register-speciality-${v.toLowerCase()}`}
                      className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm text-left transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`}>
                      <span>{l}</span>
                      {left > 0 && <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 shrink-0 ${on ? "bg-white/20" : "bg-amber-100 text-amber-900"}`} data-testid={`register-founder-left-${v.toLowerCase()}`}>Founder · {left} of 20 left</span>}
                    </button>
                  );
                })}
              </div>
              {leftFor(form.speciality) > 0 && (
                <p className="text-xs text-amber-800 mt-2 flex items-center gap-2" data-testid="register-founder-note">
                  <img src="/founders-club-logo.webp" alt="" className="w-8 h-8 rounded-full border border-amber-200 bg-white" /> You'll reserve a Founders Club place: 50% off every plan for as long as you stay fully subscribed (renewals included), plus 50% off your first policy pack. You pay from day one (no free trial) with a 12-month minimum term from your first payment — cancelling inside it forfeits the discount and the place. One place per registered location. Full terms are shown again before you pay.
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={form.password} onChange={set("password")} required
                data-testid="register-password-input" placeholder="Min. 6 characters" className="mt-1.5" />
            </div>
            <div className="rounded-xl bg-secondary/50 border border-border p-3 text-xs text-muted-foreground" data-testid="register-privacy-notice">
              <p className="font-semibold text-foreground mb-1">Privacy notice</p>
              <p>We use your name, email and organisation to run your account, answer your questions and send receipts. We never sell your data and you can delete your account at any time. Please don't type patient, child or staff identifiable details into the assistant.</p>
              <label className="flex items-start gap-2 mt-3 text-foreground cursor-pointer">
                <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" data-testid="register-consent-checkbox" />
                <span>I have read the <Link to="/privacy" className="underline" target="_blank">privacy notice</Link> and agree to the <Link to="/terms" className="underline" target="_blank">terms</Link>.</span>
              </label>
            </div>
            {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading || !consent || !form.speciality} data-testid="register-submit-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            Already registered?{" "}
            <Link to="/login" className="text-primary font-medium hover:underline" data-testid="go-to-login">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex flex-col justify-between bg-primary p-12 text-primary-foreground relative overflow-hidden order-1 lg:order-2">
        <div className="absolute inset-0 bg-gradient-to-br from-accent/40 via-primary to-primary opacity-90" />
        <Link to="/" className="relative flex items-center gap-2.5">
          <LogoMark className="w-9 h-9" />
          <span className="font-display font-extrabold tracking-tight">Futuregen Innovations</span>
        </Link>
        <div className="relative">
          <h1 className="font-display text-4xl font-extrabold tracking-tight leading-tight mb-6">
            The future of regulated service compliance.
          </h1>
          <ul className="space-y-3">
            {PERKS.map((p) => (
              <li key={p} className="flex items-start gap-3 text-primary-foreground/90">
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center mt-0.5 shrink-0">
                  <Check className="w-3 h-3" />
                </span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="relative text-sm text-primary-foreground/70">UK-based specialists</p>
      </div>
    </div>
  );
}
